import styles from './to-do-list.module.scss';
import { ToDoListTable } from './ToDoListTable';
import { useContext, useEffect, useState, useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { clsx } from 'clsx';
import { getIntermediaryToDoList } from 'bff/hooks/reporting/IntermediaryToDoList';
import {
  Search,
  SearchSize,
  ChoiceChip,
  ChoiceChipGroup,
  Icon,
  ICONS_ENUM,
  Dropdown,
  IDropdownItem,
  Pill
} from '@sanlam-glacier/app-common/components';
import WidgetError from 'modules/dashboard/components/atoms/widget-error/WidgetError';
import WidgetLoader from 'modules/dashboard/components/atoms/widget-loader/WidgetLoader';
import { WidgetContext } from 'modules/dashboard/components/organisms/widget/Widget';
import {
  ToDoListTab,
  ToDoListData,
  NegativeJourneyCategory,
  SelectedFilterCategory
} from 'modules/dashboard/types';
import { useSetWidgetQueryStatus } from 'modules/dashboard/types/widgets/widget.hooks';
import { formatToDoListData } from 'modules/dashboard/utils/formatting/to-do-list-data-formatter';
import {
  getToDoListStatusFilterRange,
  matchesSearchCriteria
} from 'modules/dashboard/utils/widget/to-do-list-widget-helpers';

export const ToDoList = () => {
  const testComponentName = 'ToDoList';

  const context = useContext(WidgetContext);

  const [selectedTab, setSelectedTab] = useState<ToDoListTab>(ToDoListTab.due);
  const [toDoListData, setToDoListData] = useState<ToDoListData>();
  const [searchTerm, setSearchTerm] = useState('');
  const [negativeJourneyType, setNegativeJourneyType] = useState<NegativeJourneyCategory>();
  const [selectedFilter, setSelectedFilter] = useState('');
  const [selectedFilterType, setSelectedFilterType] = useState<SelectedFilterCategory>();
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<(string | IDropdownItem)[]>(
    []
  );
  const [displayData, setDisplayData] = useState<ToDoListData>({
    due: [],
    overdue: [],
    notCompliant: []
  });
  const [paginationStates, setPaginationStates] = useState({
    [ToDoListTab.due]: 0,
    [ToDoListTab.overdue]: 0,
    [ToDoListTab.notCompliant]: 0
  });
  const [selectedToDoItemFiltersStates, setSelectedToDoItemFiltersStates] = useState<{
    [ToDoListTab.due]: (string | IDropdownItem)[];
    [ToDoListTab.overdue]: (string | IDropdownItem)[];
    [ToDoListTab.notCompliant]: (string | IDropdownItem)[];
  }>({
    [ToDoListTab.due]: [],
    [ToDoListTab.overdue]: [],
    [ToDoListTab.notCompliant]: []
  });

  const { isError, data, refetch, isFetching, isSuccess } = useQuery(getIntermediaryToDoList());

  const getSearchedAndFilteredData = useCallback(
    (searchTerm: string, toDoListData: ToDoListData): ToDoListData => {
      let results: ToDoListData = {
        due: [],
        overdue: [],
        notCompliant: []
      };

      if (!searchTerm || searchTerm.trim() === '') {
        results = toDoListData;
      } else {
        if (toDoListData.due)
          results.due = toDoListData.due.filter((clientEntry) =>
            matchesSearchCriteria(clientEntry, searchTerm)
          );
        if (toDoListData.overdue)
          results.overdue = toDoListData.overdue.filter((clientEntry) =>
            matchesSearchCriteria(clientEntry, searchTerm)
          );
        if (toDoListData.notCompliant)
          results.notCompliant = toDoListData.notCompliant.filter((clientEntry) =>
            matchesSearchCriteria(clientEntry, searchTerm)
          );

        if (
          results.due.length === 0 &&
          results.overdue.length === 0 &&
          results.notCompliant.length === 0
        ) {
          setNegativeJourneyType(NegativeJourneyCategory.search);
          return results;
        }
      }

      let selectedFiltersPresent = false;

      if (selectedToDoItemFiltersStates[selectedTab].length > 0) {
        selectedFiltersPresent = true;
        results = {
          ...results,
          [selectedTab]: results[selectedTab].filter((entry) =>
            selectedToDoItemFiltersStates[selectedTab].includes(entry.toDoItemTitle)
          )
        };
      }

      if (selectedTab === ToDoListTab.due && selectedStatusFilters.length > 0) {
        selectedFiltersPresent = true;

        const selectedFilters = selectedStatusFilters as IDropdownItem[];
        const valuesOfSelectedStatusFliters = [
          ...new Set(selectedFilters.map((filter) => Number(filter.value)))
        ];

        const sortedValues = valuesOfSelectedStatusFliters.slice().sort((a, b) => a - b);

        const valueRanges = getToDoListStatusFilterRange(sortedValues);
        results = {
          ...results,
          [selectedTab]: results[selectedTab].filter((entry) =>
            valueRanges.some(
              ({ min, max }) => entry.toDoItemNumOfDays >= min && entry.toDoItemNumOfDays < max
            )
          )
        };
      }

      if (
        selectedFiltersPresent &&
        (results.due.length === 0 ||
          results.overdue.length === 0 ||
          results.notCompliant.length === 0)
      ) {
        if (
          selectedToDoItemFiltersStates[selectedTab].length > 1 ||
          selectedStatusFilters.length > 1
        ) {
          setNegativeJourneyType(NegativeJourneyCategory.manyFilters);
        } else if (selectedToDoItemFiltersStates[selectedTab].length === 1) {
          setNegativeJourneyType(NegativeJourneyCategory.filter);
          const selectedFilters = selectedToDoItemFiltersStates[selectedTab] as IDropdownItem[];

          if (selectedFilters[0]?.content) {
            setSelectedFilter(selectedFilters[0]?.content);
            setSelectedFilterType(SelectedFilterCategory.toDoItem);
          }
        } else if (selectedStatusFilters.length === 1) {
          setNegativeJourneyType(NegativeJourneyCategory.filter);
          const selectedFilters = selectedStatusFilters as IDropdownItem[];

          let statusSelectedFilter = selectedFilters[0]?.content;
          if (statusSelectedFilter) {
            statusSelectedFilter = statusSelectedFilter.replace(/</g, 'in less than');
            setSelectedFilter(statusSelectedFilter);
            setSelectedFilterType(SelectedFilterCategory.status);
          }
        }
      }

      setPaginationStates((prev) => ({
        ...prev,
        [selectedTab]: 0
      }));

      return results;
    },
    [selectedToDoItemFiltersStates, selectedStatusFilters, selectedTab]
  );

  useEffect(() => {
    const formattedData = formatToDoListData(data);
    setToDoListData(formattedData);
    setDisplayData(formattedData);

    const resultsOfSearch = getSearchedAndFilteredData(searchTerm, formattedData);
    setDisplayData(resultsOfSearch);
  }, [data, searchTerm, getSearchedAndFilteredData]);

  useEffect(() => {
    if (!context) return;
    context.setReloadHandler(() => () => {
      refetch();
    });
  }, [refetch, context]);
  useSetWidgetQueryStatus({ isFetching, isError, isSuccess, context });

  const handleChoiceChipSelection = (
    _e: React.SyntheticEvent,
    value: string | string[] | undefined
  ) => {
    const selectedValue = value?.toString();
    if (selectedValue && Object.values(ToDoListTab).includes(selectedValue as ToDoListTab)) {
      setSelectedTab(selectedValue as ToDoListTab);
    }
  };

  const debouncedHandleSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    []
  );

  const handleStatusFilterSelection = (
    value: string | IDropdownItem | (string | IDropdownItem)[]
  ) => {
    if (Array.isArray(value)) {
      const selectedItems = value as IDropdownItem[];

      if (selectedItems.length === 0) {
        if (selectedStatusFilters.length > 0) {
          setSelectedStatusFilters([]);
        }
        return;
      }

      setSelectedStatusFilters((prev) => {
        const updatedFilters = selectedItems.map((item) =>
          item.content.startsWith('Due in less than')
            ? { ...item, content: item.content.replace('in less than', '<') }
            : item
        );

        const valueChanged =
          prev.length !== updatedFilters.length ||
          prev.some((item, i) => item !== updatedFilters[i]);

        return valueChanged ? updatedFilters : prev;
      });
    }
  };

  const handleToDoItemFilterSelection = (
    value: string | IDropdownItem | (string | IDropdownItem)[]
  ) => {
    const selectedFilters = value as string[];
    setSelectedToDoItemFiltersStates((prev) => ({
      ...prev,
      [selectedTab]: selectedFilters
    }));
  };

  if (isFetching) return <WidgetLoader />;
  if (isError || !toDoListData)
    return <WidgetError onRefresh={refetch} isRefreshing={isFetching} />;

  const statusFilters: IDropdownItem[] = [
    {
      content: 'Due in less than 7 days',
      value: '7',
      id: `${testComponentName}7DaysDropdownItem`,
      pill: {
        children: 'High urgency',
        size: Pill.SIZE.SMALL,
        variant: Pill.VARIANT.SECONDARY,
        className: clsx(styles.highUrgency)
      }
    },
    {
      content: 'Due in less than 30 days',
      value: '30',
      id: `${testComponentName}30DaysDropdownItem`,
      pill: {
        children: 'Medium urgency',
        size: Pill.SIZE.SMALL,
        variant: Pill.VARIANT.SECONDARY,
        className: clsx(styles.mediumUrgency)
      }
    },
    {
      content: 'Due in less than 60 days',
      value: '60',
      id: `${testComponentName}60DaysDropdownItem`,
      pill: {
        children: 'Low urgency',
        size: Pill.SIZE.SMALL,
        variant: Pill.VARIANT.SECONDARY,
        className: clsx(styles.lowUrgency)
      }
    }
  ];

  const toDoItemFilters = [
    ...new Set(toDoListData[selectedTab].map((entry) => entry.toDoItemTitle))
  ];

  return (
    <div className={styles.toDoListContainer}>
      <div className={styles.choiceChipsAndSearch}>
        <ChoiceChipGroup
          data-testid={`${testComponentName}TabOptions`}
          className={styles.choiceChips}
          defaultSelected={ToDoListTab.due}
          name='to-do-list-tabs'
          onChange={handleChoiceChipSelection}
          position={ChoiceChipGroup.POSITION.HORIZONTAL}
          size={ChoiceChipGroup.SIZE.SMALL}
        >
          <ChoiceChip
            data-testid={`${testComponentName}DueTab`}
            aria-label='due'
            displayPill
            pillLabel={displayData.due.length}
            value={ToDoListTab.due}
            inputProps={{ id: `${testComponentName}DueChip` }}
            pillProps={{ id: `${testComponentName}DueTabCount` }}
          >
            Due
          </ChoiceChip>
          <ChoiceChip
            data-testid={`${testComponentName}OverdueTab`}
            aria-label='overdue'
            displayPill
            pillLabel={displayData.overdue.length}
            value={ToDoListTab.overdue}
            inputProps={{ id: `${testComponentName}OverdueChip` }}
            pillProps={{ id: `${testComponentName}OverdueTabCount` }}
          >
            Overdue
          </ChoiceChip>
          <ChoiceChip
            data-testid={`${testComponentName}NotCompliantTab`}
            aria-label='not compliant'
            displayPill
            pillLabel={displayData.notCompliant.length}
            value={ToDoListTab.notCompliant}
            inputProps={{ id: `${testComponentName}NotCompliantChip` }}
            pillProps={{ id: `${testComponentName}NotCompliantTabCount` }}
          >
            Not compliant
          </ChoiceChip>
        </ChoiceChipGroup>

        <Search
          data-testid={`${testComponentName}SearchBar`}
          onChange={(value: string) => debouncedHandleSearch(value)}
          placeholder='Search client, investment number...'
          size={SearchSize.SMALL}
          className={styles.searchBar}
        />
      </div>
      <div className={styles.filterContainer}>
        <Icon className={styles.filterIcon} icon={ICONS_ENUM.CONTENT_FILTER_LIST} />
        <Dropdown
          data-testid={`${testComponentName}ToDoItemFilter`}
          multiSelect={true}
          placeholder={
            selectedToDoItemFiltersStates[selectedTab].length === 0 ? 'To-do item' : undefined
          }
          onChange={handleToDoItemFilterSelection}
          popperPlacement={Dropdown.POSITION.BOTTOM}
          chipLimit={2}
          items={toDoItemFilters}
          className={styles.toDoItemFilterDropDown}
          selected={selectedToDoItemFiltersStates[selectedTab]}
          inputVariant={Dropdown.VARIANT.TABLE}
          maxHeight={264}
          iconButtonProps={{ id: `${testComponentName}ToDoItemFilterButton` }}
          inputProps={{ id: `${testComponentName}ToDoItemFilterPlaceholder` }}
          pureFormInputProps={{ id: `${testComponentName}ToDoItemFilterFormInput` }}
        />
        {selectedTab === ToDoListTab.due && (
          <Dropdown
            data-testid={`${testComponentName}StatusFilter`}
            multiSelect={true}
            placeholder={selectedStatusFilters.length === 0 ? 'Status' : undefined}
            onChange={(value) => handleStatusFilterSelection(value)}
            popperPlacement={Dropdown.POSITION.BOTTOM}
            chipLimit={2}
            items={statusFilters}
            className={styles.statusFilterDropDown}
            selected={selectedStatusFilters}
            inputVariant={Dropdown.VARIANT.TABLE}
            maxHeight={208}
            iconButtonProps={{ id: `${testComponentName}StatusFilterButton` }}
            inputProps={{ id: `${testComponentName}StatusFilterPlaceholder` }}
            pureFormInputProps={{ id: `${testComponentName}StatusFilterFormInput` }}
          />
        )}
      </div>
      <ToDoListTable
        selectedTab={selectedTab}
        entries={displayData[selectedTab]}
        currentPage={paginationStates[selectedTab]}
        setSelectedTabCurrentPage={(page: number) =>
          setPaginationStates((prev) => ({
            ...prev,
            [selectedTab]: page
          }))
        }
        negativeJourneyType={negativeJourneyType}
        searchTerm={searchTerm}
        selectedFilter={selectedFilter}
        selectedFilterType={selectedFilterType}
      />
    </div>
  );
};
